import { trackEvent } from '@/utils/analytics'
import EmptyDataRow from '@/components/DataTable/EmptyDataRow'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { OrganizationUser, formatUserRole, type UserRole } from '@/types/users'
import { Edit, Plus, Trash, Loader2, Search, XCircle } from 'lucide-react'
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react'
import { Input } from '@/components/ui/input'
import { RowAction } from '@/components/DataTable/DataTableTypes'
import RowActionsDropdown from '@/components/DataTable/RowActionsDropdown'
import { useAppStore } from '@/store'
import InviteUserModal from './InviteUserModal'
import EditUserModal from './EditUserModal'
import RevokeInviteDialog from './RevokeInviteDialog'
import { toast } from 'sonner'
import { formatDateTime } from '@/utils/timeUtils'
import { useTranslation } from '@/i18n'

export default function Team() {
  const { t } = useTranslation()
  const {
    fetchOrganizationUsers,
    inviteUserToOrganization,
    deleteOrganizationUser,
    updateUserRole,
    revokeOrganizationInvite,
    organizationUsers,
    isUsersLoading,
    user,
  } = useAppStore()
  const isAdmin = user?.role === 'admin'
  const USERS_COLS = [
    t('common:settings.team.table_headers.name'),
    t('common:settings.team.table_headers.email'),
    t('common:settings.team.table_headers.role'),
    t('common:settings.team.table_headers.added_on'),
    t('common:settings.team.table_headers.status'),
    ...(isAdmin ? [''] : []),
  ]
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showRevokeModal, setShowRevokeModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(null)
  const [selectedInvite, setSelectedInvite] = useState<OrganizationUser | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return organizationUsers
    const searchLower = searchTerm.toLowerCase()
    return organizationUsers.filter(
      (user) =>
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
    )
  }, [organizationUsers, searchTerm])

  useEffect(() => {
    fetchOrganizationUsers()
  }, [fetchOrganizationUsers])

  const handleInviteUser = async (userData: {
    email: string
    role: UserRole
  }) => {
    await inviteUserToOrganization(userData)
    trackEvent('settings-team_member_invited', { role: userData.role, invite_source: 'direct' })
  }

  const handleEditUser = async (
    userId: string,
    role: UserRole
  ) => {
    await updateUserRole(userId, role)
  }

  const handleRevokeInvite = async () => {
    if (!selectedInvite?.invite_id) {
      toast.error(t('common:settings.team.errors.user_not_found'))
      return
    }

    try {
      await revokeOrganizationInvite(selectedInvite.invite_id)
      setShowRevokeModal(false)
      setSelectedInvite(null)
    } catch(error) {
      console.error(error)
    }
  }

  const statusBadgeClassName = (status?: string | null) => {
    if (status === 'active') {
      return 'text-green-600 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
    }
    if (status === 'pending') {
      return 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
    }
    return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-dark-muted border-gray-200 dark:border-dark-border'
  }

  const buildMenuActions = (organizationUser: OrganizationUser): RowAction[] => {
    if (organizationUser.status === 'pending') {
      return [
        {
          name: t('common:settings.team.actions.revoke_invite'),
          icon: <XCircle className="w-4 h-4 me-1 cursor-pointer" />,
          action: (
            e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
            id: string
          ) => {
            e.stopPropagation()
            setSelectedInvite(
              organizationUsers.find((user) => user.invite_id === id || user.id === id) || null
            )
            setShowRevokeModal(true)
          },
        },
      ]
    }

    return [
      {
        name: t('common:settings.team.actions.edit'),
        icon: <Edit className="w-4 h-4 me-1 cursor-pointer" />,
        action: (
          e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
          id: string
        ) => {
          e.stopPropagation()
          setSelectedUser(
            organizationUsers.find((user) => user.user_id === id || user.id === id) || null
          )
          setShowEditModal(true)
        },
      },
      {
        name: t('common:settings.team.actions.delete'),
        icon: <Trash className="w-4 h-4 cursor-pointer" />,
        action: async (
          e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
          id: string
        ) => {
          e.stopPropagation()
          const userToDelete = organizationUsers.find(
            (user) => user.user_id === id || user.id === id
          )
          if (userToDelete?.user_id) {
            const success = await deleteOrganizationUser(userToDelete.user_id)
            if (success) {
              trackEvent('settings-team_member_removed', {})
            }
          } else {
            toast.error(t('common:settings.team.errors.user_not_found'))
          }
        },
      },
    ]
  }

  return (
    <>
      <div className="border border-[#E4E7EC] dark:border-dark-border rounded-2xl shadow-[0,1px,2px,0,rgba(16,24,40,0.05)] px-6 py-5 bg-white dark:bg-dark-card">
        <div className="pb-5 border-b border-[#E4E7EC] dark:border-dark-border">
          <div className="flex flex-col md:flex-row gap-4 items-start justify-between">
            <div>
              <div className="text-lg text-[#101828] dark:text-dark-text font-semibold mb-1">
                {t('common:settings.team.title')}
              </div>
              <div className="text-sm text-[#475467] dark:text-gray-400">
                {t('common:settings.team.description')}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative w-72">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <Input
                  placeholder={t('common:settings.team.search_placeholder')}
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  className="ps-9"
                />
              </div>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowInviteModal(true)}>
                  <Plus className="w-4 h-4" />
                  <div className="text-sm font-semibold">
                    {t('common:settings.team.add_user')}
                  </div>
                </Button>
              )}
            </div>
          </div>
        </div>
        <Table>
          <TableHeader className="bg-slate-50 dark:bg-dark-muted">
            <TableRow>
              {USERS_COLS.map((col) => (
                <TableHead
                  key={col}
                  className={`${
                    col === 'Actions' ? 'w-[140px] bg-slate-50 dark:bg-dark-muted' : ''
                  } py-3 px-6`}
                >
                  {col}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isUsersLoading ? (
              <TableRow>
                <TableCell
                  colSpan={USERS_COLS.length}
                  className="text-center py-8"
                >
                  <div className="flex justify-center items-center">
                    <Loader2 className="w-6 h-6 animate-spin me-2" />
                    <span>{t('common:settings.team.loading_members')}</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredUsers?.length > 0 ? (
              filteredUsers.map((organizationUser) => (
                <TableRow key={organizationUser.user_id || organizationUser.id}>
                  <TableCell className="!p-6">
                    <span className="font-medium">
                      {organizationUser.name || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell className="!p-6">
                    {organizationUser.email || 'N/A'}
                  </TableCell>
                  <TableCell className="!p-6">
                    {formatUserRole(organizationUser.role)}
                  </TableCell>
                  <TableCell className="!p-6">
                    {formatDateTime(organizationUser.joined_at || '') || 'N/A'}
                  </TableCell>
                  <TableCell className="!p-6">
                    <span
                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium capitalize ${statusBadgeClassName(
                        organizationUser.status
                      )}`}
                    >
                      {organizationUser.status || 'N/A'}
                    </span>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="!p-6 w-[20px]">
                      {organizationUser.user_id !== user?.userId &&
                        <RowActionsDropdown
                          id={organizationUser.user_id || organizationUser.id || ''}
                          actions={buildMenuActions(organizationUser)}
                          rowType="user"
                        />
                      }
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <EmptyDataRow
                message={t('common:settings.team.no_users_found')}
                colSpan={USERS_COLS.length}
              />
            )}
          </TableBody>
        </Table>
      </div>
      {isAdmin && (
        <>
          <InviteUserModal
            isOpen={showInviteModal}
            onClose={() => setShowInviteModal(false)}
            onSubmit={handleInviteUser}
          />
          <EditUserModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false)
              setSelectedUser(null)
            }}
            onSubmit={handleEditUser}
            selectedUser={selectedUser}
          />
          <RevokeInviteDialog
            isOpen={showRevokeModal}
            email={selectedInvite?.email || ''}
            onClose={() => {
              setShowRevokeModal(false)
              setSelectedInvite(null)
            }}
            onConfirm={handleRevokeInvite}
          />
        </>
      )}
    </>
  )
}
